import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';

import {
  Alert,
  Checkbox,
  Col,
  Form,
  FormInstance,
  Radio,
  Row,
  Select,
} from 'antd';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import './style.less';
import {
  CompanySintegraGenerateFormValues,
  InventorySelectionMode,
  invoiceTypeSintegra,
} from '@/core/domain/sintegra/Sintegra.types';
import InventoryInfiniteSelectField from '@/components/_common/InventoryInfiniteSelectField';
import { useDefaultEmptyFieldError } from '@/hooks';
import moment from 'moment';

type Props = {
  form: FormInstance<CompanySintegraGenerateFormValues>;
};

const SintegraForm = ({ form }: Props) => {
  const intl = useIntl().formatMessage;

  const companyId = Form.useWatch('companyId', form);
  const startDate = Form.useWatch('startDate', form);
  const inventorySelectionMode = Form.useWatch('inventorySelectionMode', form);
  const completeWithExempt = Form.useWatch('completeWithExempt', form);

  const [record74Visible, setRecord74Visible] = useState(false);

  const noteTypeOptions = [
    {
      label: 'Peças',
      value: invoiceTypeSintegra.NFE,
    },
    {
      label: 'Serviços',
      value: invoiceTypeSintegra.NFS,
    },
    {
      label: 'Peças e Serviços',
      value: invoiceTypeSintegra.ALL,
    },
  ];

  const PurposeOfPresentationOptions = [
    {
      label: 'Normal',
      value: '1',
    },
    {
      label: 'Retificação total de arquivo',
      value: '2',
    },
  ];

  useEffect(() => {
    form.setFieldValue('endDate', undefined);
  }, [startDate]);

  const ruleRequired = useDefaultEmptyFieldError();

  return (
    <Form form={form} layout="vertical">
      <Row gutter={[16, 16]}>
        <Col span={24} md={12} xl={8}>
          <FloatingFormItem label="Data Inicial" name="startDate">
            <InputDate />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} xl={8}>
          <FloatingFormItem
            label="Data Final"
            name="endDate"
            rules={[
              {
                validator: async (_, value) => {
                  if (value?.isAfter(moment(startDate).add(1, 'month'))) {
                    return Promise.reject(
                      new Error(
                        'Data final deve ser até um mês após a data inicial.',
                      ),
                    );
                  }
                },
              },
            ]}
          >
            <InputDate
              disabled={!startDate}
              disabledDate={(date) =>
                !!startDate &&
                (date.isBefore(startDate) ||
                  date.isAfter(moment(startDate).add(1, 'month')))
              }
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={24} xl={8}>
          <FloatingFormItem
            label={intl({
              id: 'groupings.form.company',
            })}
            name="companyId"
            rules={[ruleRequired]}
          >
            <CompaniesInfiniteSelectField />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={8} xl={6} className="centralize-container">
          <Form.Item
            valuePropName="checked"
            name="filterByReleaseDate"
            initialValue={false}
          >
            <Checkbox>Filtrar por data de lançamento</Checkbox>
          </Form.Item>
        </Col>

        <Col span={24} md={8} xl={5} className="centralize-container">
          <Form.Item
            valuePropName="checked"
            name="generateRecord74"
            initialValue={false}
          >
            <Checkbox onChange={() => setRecord74Visible(!record74Visible)}>
              Gerar registro74
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={24} md={8} xl={5} className="centralize-container">
          <Form.Item
            valuePropName="checked"
            name="generateRecord61R"
            initialValue={false}
          >
            <Checkbox> Gerar registro 61R</Checkbox>
          </Form.Item>
        </Col>

        <Col span={24} md={24} xl={8}>
          <Form.Item
            label="Tipo de Nota"
            name="invoiceType"
            initialValue={invoiceTypeSintegra.NFE}
          >
            <Radio.Group
              buttonStyle="solid"
              optionType="button"
              options={noteTypeOptions}
            />
          </Form.Item>
        </Col>

        {record74Visible && (
          <>
            <Col span={24} md={12} xl={12}>
              <FloatingFormItem
                name="inventorySelectionMode"
                label="Buscar dados de inventário de"
                initialValue={InventorySelectionMode.SPECIFIC_INVENTORY}
              >
                <Select
                  allowClear
                  options={[
                    {
                      value: InventorySelectionMode.CURRENT_STOCK,
                      label: 'Estoque Atual',
                    },
                    {
                      value: InventorySelectionMode.SPECIFIC_INVENTORY,
                      label: 'Inventário',
                    },
                  ]}
                />
              </FloatingFormItem>
            </Col>

            {inventorySelectionMode ===
              InventorySelectionMode.SPECIFIC_INVENTORY && (
              <Col span={24} md={12} xl={12}>
                <FloatingFormItem
                  name="inventoryId"
                  label="Inventário"
                  rules={[ruleRequired]}
                >
                  <InventoryInfiniteSelectField
                    disabled={!companyId}
                    companyId={companyId}
                  />
                </FloatingFormItem>
              </Col>
            )}

            {inventorySelectionMode ===
              InventorySelectionMode.CURRENT_STOCK && (
              <Col span={24} md={12} xl={12}>
                <FloatingFormItem
                  name="inventoryDate"
                  label="Data do inventário"
                  rules={[ruleRequired]}
                >
                  <InputDate />
                </FloatingFormItem>
              </Col>
            )}
          </>
        )}

        <Col span={24} md={24} xl={12}>
          <FloatingFormItem
            name="filePurpose"
            label="Finalidade da apresentação do arquivo eletrônico"
            rules={[ruleRequired]}
          >
            <Select options={PurposeOfPresentationOptions} />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} xl={6} className="centralize-container">
          <Form.Item
            valuePropName="checked"
            name="removePartStereotypes"
            initialValue={false}
          >
            <Checkbox>Remover estereótipo de peças</Checkbox>
          </Form.Item>
        </Col>

        <Col span={24} md={12} xl={6} className="centralize-container">
          <Form.Item
            valuePropName="checked"
            name="removeConsumableStereotypes"
            initialValue={false}
          >
            <Checkbox>Remover estereótipo de insumos</Checkbox>
          </Form.Item>
        </Col>

        <Col span={24} lg={8}>
          <Form.Item
            valuePropName="checked"
            name="completeWithExempt"
            initialValue={false}
          >
            <Checkbox>
              Preencher Inscrição Estadual como "ISENTO" para clientes isentos
            </Checkbox>
          </Form.Item>

          {!!completeWithExempt && (
            <Alert
              message="Atenção!"
              type="warning"
              showIcon
              description={
                <p>
                  Com essa opção marcada, o campo de Inscrição Estadual será
                  preenchido com "<strong>ISENTO</strong>" para clientes que não
                  possuem inscrição estadual e que estejam com o tipo de
                  contribuinte isento ou não contribuinte na Nota Fiscal.
                  Certifique-se de validar o arquivo com o Validador Sintegra.
                </p>
              }
            />
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default SintegraForm;
